import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as allergyApi from "@/api/allergy.api";
import { mutationMeta } from "@/lib/query-client";
import type { CreateAllergyRequest, UpdateAllergyRequest } from "@/types";

export function useAllergies(
  patientId: string | undefined,
  params?: { page?: number; per_page?: number; status?: string },
) {
  return useQuery({
    queryKey: ["allergies", patientId, params],
    queryFn: () => allergyApi.listAllergies(patientId!, params),
    enabled: !!patientId,
  });
}

export function useActiveAllergies(patientId: string | undefined) {
  return useQuery({
    queryKey: ["allergies", "active", patientId],
    queryFn: () => allergyApi.listActiveAllergies(patientId!),
    enabled: !!patientId,
  });
}

export function useCreateAllergy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      data,
    }: {
      patientId: string;
      data: CreateAllergyRequest;
    }) => allergyApi.createAllergy(patientId, data),
    meta: mutationMeta.created("Allergy"),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["allergies", variables.patientId],
      });
    },
  });
}

export function useUpdateAllergy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAllergyRequest }) =>
      allergyApi.updateAllergy(id, data),
    meta: mutationMeta.updated("Allergy"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allergies"] });
    },
  });
}

export function useDeleteAllergy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => allergyApi.deleteAllergy(id),
    meta: mutationMeta.deleted("Allergy"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allergies"] });
    },
  });
}

export function usePatientAllergyStatus(patientId: string | undefined) {
  return useQuery({
    queryKey: ["allergies", "status", patientId],
    queryFn: () => allergyApi.getPatientAllergyStatus(patientId!),
    enabled: !!patientId,
    retry: (count, err: unknown) => {
      const status = (err as { status?: number })?.status;
      if (status === 404) return false;
      return count < 2;
    },
  });
}

export function useSetPatientAllergyStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ patientId, nkda }: { patientId: string; nkda: boolean }) =>
      allergyApi.setPatientAllergyStatus(patientId, nkda),
    meta: mutationMeta.updated("Allergy status"),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["allergies", "status", variables.patientId],
      });
      queryClient.invalidateQueries({
        queryKey: ["allergies", variables.patientId],
      });
    },
  });
}

/** Search seeded allergen catalog (RxNorm / SNOMED reference list). */
export function useAllergenCatalog(type: string, query: string) {
  return useQuery({
    queryKey: ["allergen-catalog", type, query],
    queryFn: () =>
      allergyApi.searchAllergenCatalog({
        type: type || undefined,
        q: query || undefined,
        limit: 40,
      }),
    staleTime: 60_000,
  });
}
